import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  Empty,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  notification,
} from "antd";
import {
  CopyOutlined,
  ExportOutlined,
  ImportOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { ApiService } from "../../api";
import { OpenAPI } from "../../api/core/OpenAPI";
import { apiErrorMessage } from "../../api/apiError";
import CloneTemplateModal from "../../components/CloneTemplateModal";
import { useRefreshOnTabFocus } from "../../tabs/tabContext";
import { PageHeader } from "../../components/PageHeader";

type TemplateRow = {
  id: number;
  name: string;
  short_name: string;
  type: string;
};

/**
 * The templates this user administers.
 *
 * Scoped by the same `type=admin` call the navigation uses, so somebody who
 * administers one permit type is not shown every template in the system.
 */
const TemplateListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rows, setRows] = useState<TemplateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const types = await ApiService.apiMainTemplateTypesList("admin");
        const flat: TemplateRow[] = [];
        (types as any[]).forEach((type) =>
          (type.filtered_templates ?? []).forEach((tpl: any) =>
            flat.push({
              id: tpl.id,
              name: tpl.name,
              short_name: tpl.short_name,
              type: type.name,
            })
          )
        );
        setRows(flat);
      } catch {
        setRows([]);
      }
      setLoading(false);
    })();
  }, [reload]);

  // A template created in another tab should appear on returning here.
  useRefreshOnTabFocus(() => setReload((n) => n + 1));

  // Above the loading early-return, not below it: a hook after a conditional
  // return runs in a different order on the two paths, which is the one React
  // rule the compiler refuses outright.
  /** Which row's "make a copy" dialog is open. */
  const [cloning, setCloning] = useState<TemplateRow | undefined>();
  const fileInput = useRef<HTMLInputElement>(null);


  if (loading && rows.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  /**
   * Download one process as a file.
   *
   * Fetched with the session's token and saved from a blob rather than
   * navigated to: `window.location.href` sends no Authorization header, so the
   * download would 401 -- the same trap the PWA's document downloads hit.
   */
  const exportTemplate = async (row: TemplateRow) => {
    try {
      const response = await fetch(
        `${OpenAPI.BASE}/api/main/templates/${row.id}/export/`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("access")}` } }
      );
      if (!response.ok) throw new Error(String(response.status));
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${row.short_name}-process.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      notification.error({ message: t("That process could not be exported.") });
    }
  };

  const importFile = async (file: File) => {
    let parsed: any;
    try {
      parsed = JSON.parse(await file.text());
    } catch {
      // Said here rather than sent to the server: a file that is not JSON at
      // all is not a process the server should have to have an opinion about.
      notification.error({
        message: t("That file is not a process export."),
      });
      return;
    }
    try {
      const created = await ApiService.apiMainTemplateImportCreate({
        process: parsed,
      });
      notification.success({
        message: t("'{{name}}' was imported.", { name: created.name }),
        description: created.warnings.length
          ? created.warnings.join(" ")
          : undefined,
        duration: created.warnings.length ? 12 : 4,
      });
      setReload((n) => n + 1);
    } catch (e: any) {
      const body = e?.body ?? {};
      notification.error({
        message: apiErrorMessage(e, t("That process could not be imported.")),
        description: (body.problems ?? []).join(" ") || undefined,
        duration: 12,
      });
    }
  };

  return (
    <div>
      <PageHeader
        title={t("Templates")}
        actions={
          <Space>
            {/* Importing is the second way a process arrives here, and it
                belongs beside the first rather than buried in a settings
                screen: "somebody sent me a process" is exactly as ordinary as
                "I am starting a new one". */}
            <Button icon={<ImportOutlined />} onClick={() => fileInput.current?.click()}>
              {t("Import")}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate("/templates/create")}
            >
              {t("New template")}
            </Button>
          </Space>
        }
      />

      {/* Hidden, and driven by the button above: a bare file input is the one
          control in antd's vocabulary that cannot be made to look like the
          rest of the page. */}
      <input
        ref={fileInput}
        type="file"
        accept="application/json,.json"
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Cleared immediately so choosing the same file twice still fires --
          // a change event needs a change, and re-importing after fixing a
          // group is a completely ordinary thing to do.
          e.target.value = "";
          if (file) importFile(file);
        }}
      />

      <Card size="small">
        {rows.length === 0 ? (
          <Empty description={t("No templates you can administer")} />
        ) : (
          <Table
            size="small"
            rowKey="id"
            dataSource={rows}
            loading={loading}
            pagination={false}
            columns={[
              {
                title: t("Name"),
                render: (_: unknown, row: TemplateRow) => (
                  <Button
                    type="link"
                    style={{ padding: 0, height: "auto" }}
                    onClick={() => navigate(`/templates/${row.short_name}/setup`)}
                  >
                    {t(row.name)}
                  </Button>
                ),
              },
              {
                title: t("Short name"),
                width: 140,
                render: (_: unknown, row: TemplateRow) => <Tag>{row.short_name}</Tag>,
              },
              {
                title: t("Type"),
                width: 200,
                render: (_: unknown, row: TemplateRow) => t(row.type),
              },
              {
                title: t("Actions"),
                width: 300,
                render: (_: unknown, row: TemplateRow) => (
                  <Space size={4}>
                    <Button
                      size="small"
                      onClick={() =>
                        navigate(`/templates/${row.short_name}/setup`)
                      }
                    >
                      {t("Set up")}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => navigate(`/templates/${row.id}/process`)}
                    >
                      {t("Process")}
                    </Button>
                    <Tooltip title={t("Make a full copy of this process")}>
                      <Button
                        size="small"
                        icon={<CopyOutlined />}
                        aria-label={t("Copy this process")}
                        onClick={() => setCloning(row)}
                      />
                    </Tooltip>
                    <Tooltip
                      title={t(
                        "Download this process as a file you can keep or send to another site"
                      )}
                    >
                      <Button
                        size="small"
                        icon={<ExportOutlined />}
                        aria-label={t("Export")}
                        onClick={() => exportTemplate(row)}
                      />
                    </Tooltip>
                  </Space>
                ),
              },
            ]}
          />
        )}
      </Card>

      <CloneTemplateModal
        templateId={cloning?.id}
        templateName={cloning?.name}
        shortName={cloning?.short_name}
        visible={cloning !== undefined}
        setVisible={(open) => !open && setCloning(undefined)}
        onDone={() => setReload((n) => n + 1)}
      />
    </div>
  );
};

export default TemplateListPage;
